import {AxiosInstance} from "axios";
import {Pet, PetCategory, PetLifestyle, PetLifestylePayload} from "@/customer/interfaces/Pet";

export default function (hdsClient: AxiosInstance) {
    return {
        /**
         * Fetch the pets reachable by the current organization.
         * @param params  The query parameters, to scope the list to a customer.
         */
        async index(params: {customer_id?: number} = {}): Promise<Pet[]> {
            return hdsClient.get(`pets`, {params})
                .then((response) => response.data.data);
        },

        /**
         * Fetch a pet by id.
         * @param petId
         */
        async read(petId: number): Promise<Pet> {
            return hdsClient.get(`pets/${petId}`)
                .then((response) => response.data.data);
        },

        /**
         * Create or update the lifestyle of a pet.
         * @param petId  The pet id.
         * @param payload  The lifestyle attributes to store.
         */
        async updateLifestyle(petId: number, payload: PetLifestylePayload): Promise<PetLifestyle> {
            return hdsClient.put(`pets/${petId}/lifestyle`, payload)
                .then((response) => response.data.data);
        },

        /**
         * Update a pet.
         * @param petId  The pet id.
         * @param data  The pet data to update.
         */
        async update(petId: number, data: Partial<Pet>): Promise<Pet> {
            return hdsClient.patch(`pets/${petId}`, data)
                .then((response) => response.data.data);
        },

        /**
         * Delete a pet.
         * @param petId  The pet id.
         */
        async delete(petId: number): Promise<void> {
            return hdsClient.delete(`pets/${petId}`)
                .then((response) => response.data.data);
        },

        /**
         * Create a pet.
         * @param data  The pet data to create.
         */
        async create(data: Partial<Pet> & {customer_id: number}): Promise<Pet> {
            return hdsClient.post(`pets`, data)
                .then((response) => response.data.data);
        },

        /**
         * Upload a pet image
         * @param petId  The pet id.
         * @param files  The files to upload.
         */
        async upload(petId: number, files: FileList | null): Promise<void> {
            if (!files) {
                return;
            }

            const data = new FormData();
            data.append('image', files[0]);

            return hdsClient.post(`pets/${petId}/upload`, data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        },

        /**
         * Fetch the pet image.
         * @param petId  The pet id.
         * @param cache  Whether to cache the image or not. Defaults to true.
         */
        async image(petId: number, cache: boolean = true): Promise<Blob> {
            return hdsClient.get(`pets/${petId}/image`, {
                responseType: 'blob',
                headers: {
                    'Cache-Control': cache ? 'public, max-age=31536000' : 'no-cache'
                }
            })
                .then((response) => response.data);
        },

        /**
         * Fetch the pet categories.
         */
        async categories(): Promise<PetCategory[]> {
            return hdsClient.get(`pet-categories`)
                .then((response) => response.data.data);
        }
    };
}
